/**
 * SidebarInboxButton - the inbox affordance in the rail and mobile sidebar.
 *
 * A quiet icon that navigates to the `/inbox` page, with a count badge for
 * unread items. Desktop uses the shared rail item; mobile keeps a header button.
 *
 * Renders nothing when the server has no inbox feature; a transient fetch
 * failure keeps the button (the problem is shown on the page, not by erasing
 * chrome).
 *
 * @module components/sidebar/SidebarInboxButton
 */
import { Link, useLocation } from "@tanstack/react-router";
import { InboxIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { isInboxPathname } from "../inbox/inboxPaths";
import { useInboxUnread } from "../inbox/useInboxUnread";
import { InboxRailIcon } from "../icons/RailIcons";
import { NavRailItem, NavRailIconWithCount } from "../NavRailItem";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { useSidebar } from "../ui/sidebar";

export function SidebarInboxButton({
  onBackdrop = false,
  rail = false,
  bigger = false,
}: {
  readonly onBackdrop?: boolean;
  readonly rail?: boolean;
  readonly bigger?: boolean;
}) {
  const { count, available, isPending } = useInboxUnread();
  const { isMobile, setOpenMobile } = useSidebar();
  const isActive = useLocation({ select: (location) => isInboxPathname(location.pathname) });

  // No inbox on this server → no button. (Not even a disabled one.)
  if (!available && !isPending) {
    return null;
  }

  if (rail) {
    return (
      <NavRailItem
        label="Inbox"
        icon={InboxRailIcon}
        // Deliberately org-less: this is the personal inbox across every
        // company (cross-company invitations live here), and "/$org/inbox"
        // would narrow it. It keeps your company by not changing it — the
        // rail's other links read it from state while you are here.
        to="/inbox"
        isActive={isActive}
        bigger={bigger}
        count={count}
        countLabel="unread notifications"
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={count > 0 ? `Inbox, ${count} unread notifications` : "Inbox"}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              // `group` arms the counter's hover cue, as it does on the rail.
              "group relative z-10",
              isActive && "bg-sidebar-row-selected text-sidebar-foreground",
              onBackdrop &&
                "[:hover,[data-pressed]]:bg-white/15 [&_svg]:stroke-white/90! [&_svg]:hover:stroke-white!",
            )}
            size="icon-sm"
            variant="ghost-muted"
            render={
              <Link
                to="/inbox"
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                }}
              >
                <NavRailIconWithCount icon={InboxIcon} iconClassName="size-4" count={count} />
              </Link>
            }
          />
        }
      />
      <TooltipPopup side="bottom">Inbox</TooltipPopup>
    </Tooltip>
  );
}
